import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { PageWrapper, Card, Button, Input, MembershipBadge, Logo, FooterBranding, ReferralCard, NotificationSettings, AlignmentHistoryChart } from "../components";
import StartYourCirclePerk from "../components/StartYourCirclePerk";
import { Pencil, Crown, Star, CreditCard, HelpCircle, LogOut, Settings, Mic, ChevronRight, Check, Mountain, Share2, RotateCcw, Image as ImageIcon } from "lucide-react";
import { resolveBrand, isBrandedNarrow } from "../lib/brand";
import SoulumoAccountPage from "./Soulumo/SoulumoAccountPage";

// Smooth animation config
const smoothTransition = { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] };
const staggerContainer = { animate: { transition: { staggerChildren: 0.08 } } };
const fadeInUp = { 
  initial: { opacity: 0, y: 16 }, 
  animate: { opacity: 1, y: 0 },
  transition: smoothTransition
};

export const AccountPage = () => {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  // Feb 2026 · When served from soulumo.app (or any other branded
  // narrow host), render the Soulumo-only Account page so the user
  // never sees Reclaim chrome (Reclaim logo, Programs/Challenges
  // bottom nav, Reclaim onboarding links, Circles perk, etc).
  const brand = resolveBrand();
  if (isBrandedNarrow(brand) && brand === "soulumo") {
    return <SoulumoAccountPage />;
  }

  const handleSave = async () => {
    setSaving(true);
    await updateProfile({ name });
    setSaving(false);
    setEditing(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <PageWrapper>
      <motion.div 
        initial="initial"
        animate="animate"
        variants={staggerContainer}
        className="p-6 space-y-6 max-w-lg mx-auto pb-28"
      >
        {/* Header with Logo */}
        <motion.div variants={fadeInUp} className="flex items-center justify-center pt-2">
          <Logo size="md" variant="dark" />
        </motion.div>

        <motion.div variants={fadeInUp} className="space-y-1">
          <h1 className="font-heading text-3xl text-[#292524]">Account</h1>
          <p className="text-[#78716C]">Manage your profile and preferences</p>
        </motion.div>

        {/* Profile Card */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4">
            <div className="flex items-center gap-4">
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#4A6741] to-[#3D5736] rounded-full flex items-center justify-center text-white font-heading text-2xl shadow-lg shadow-[#4A6741]/20">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                {user?.membership_tier === "paid" && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-[#D4A373] to-[#B8956A] rounded-full flex items-center justify-center shadow-md"
                  >
                    <Crown size={12} className="text-white" />
                  </motion.div>
                )}
              </motion.div>
              <div className="flex-1">
                <AnimatePresence mode="wait">
                  {editing ? (
                    <motion.div
                      key="editing"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        data-testid="edit-name"
                        autoFocus
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="display"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading text-xl text-[#292524]">{user?.name}</h3>
                        <MembershipBadge tier={user?.membership_tier} />
                      </div>
                      <p className="text-[#78716C] text-sm">{user?.email}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            
            <AnimatePresence mode="wait">
              {editing ? (
                <motion.div 
                  key="edit-actions"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex gap-3"
                >
                  <Button 
                    variant="outline" 
                    onClick={() => { setEditing(false); setName(user?.name || ""); }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} className="flex-1" disabled={saving} data-testid="save-profile-btn">
                    {saving ? "Saving..." : <><Check size={16} /> Save</>}
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="edit-btn"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                >
                  <Button variant="outline" onClick={() => setEditing(true)} className="w-full" data-testid="edit-profile-btn">
                    <Pencil size={16} /> Edit Profile
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        {/* Membership Card */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4 overflow-hidden">
            <div className="flex items-center gap-4">
              <motion.div 
                className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm ${
                  user?.membership_tier === "paid" 
                    ? "bg-gradient-to-r from-[#D4A373] to-[#B8956A] shadow-[#D4A373]/20" 
                    : "bg-[#E6E2DD]"
                }`}
                whileHover={{ scale: 1.05, rotate: user?.membership_tier === "paid" ? 5 : 0 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {user?.membership_tier === "paid" ? (
                  <Crown className="text-white" size={22} />
                ) : (
                  <Star className="text-[#78716C]" size={22} />
                )}
              </motion.div>
              <div className="flex-1">
                <h3 className="font-heading text-lg text-[#292524]">
                  {user?.membership_tier === "paid" ? "Premium Member" : "Free Member"}
                </h3>
                <p className="text-sm text-[#78716C]">
                  {user?.membership_tier === "paid" 
                    ? "Full access to all features" 
                    : "Upgrade for full access"}
                </p>
              </div>
            </div>
            {user?.membership_tier !== "paid" ? (
              <Button variant="premium" onClick={() => navigate("/upgrade")} className="w-full" data-testid="upgrade-membership-btn">
                <Crown size={16} /> Upgrade to Premium
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/upgrade")} className="w-full" data-testid="manage-billing-btn">
                <CreditCard size={16} /> View Membership
              </Button>
            )}
          </Card>
        </motion.div>

        {/* Share Your Journey Card */}
        <motion.div variants={fadeInUp}>
          <Card className="p-5 space-y-4 bg-gradient-to-br from-[#F9FAFB] to-[#F3F4F6] border-[#E6E2DD]">
            <div className="flex items-center gap-4">
              <motion.div 
                className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md shadow-amber-500/20"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Mountain className="text-white" size={22} />
              </motion.div>
              <div className="flex-1">
                <h3 className="font-heading text-lg text-[#292524]">Share Your Journey</h3>
                <p className="text-sm text-[#78716C]">
                  Inspire others with your progress
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate("/hero-journey/share")} 
              className="w-full border-amber-500/30 text-amber-700 hover:bg-amber-50" 
              data-testid="share-journey-btn"
            >
              <Share2 size={16} /> Create Shareable Card
            </Button>
          </Card>
        </motion.div>

        {/* Alignment History Chart */}
        <motion.div variants={fadeInUp}>
          <AlignmentHistoryChart />
        </motion.div>

        {/* Referral Card - Invite Friends */}
        <motion.div variants={fadeInUp}>
          <ReferralCard />
        </motion.div>

        {/* Partner perk · Start your own Circle */}
        <motion.div variants={fadeInUp}>
          <StartYourCirclePerk variant="card" />
        </motion.div>

        {/* Daily Ritual Reminder Settings */}
        <motion.div variants={fadeInUp}>
          <NotificationSettings />
        </motion.div>

        {/* Other Settings */}
        <motion.div variants={fadeInUp}>
          <Card className="divide-y divide-[#E6E2DD] overflow-hidden">
            {/* My Library Link */}
            <motion.button
              onClick={() => navigate("/library")}
              className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] transition-all duration-200"
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.99 }}
              data-testid="my-library-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E40FF]/10 flex items-center justify-center">
                  <ImageIcon className="text-[#1E40FF]" size={18} />
                </div>
                <div className="text-left">
                  <span className="text-[#292524] block font-medium">My Library</span>
                  <span className="text-xs text-[#78716C]">Saved snapshots, vision images & generated visuals</span>
                </div>
              </div>
              <ChevronRight size={18} className="text-[#9CA3AF]" />
            </motion.button>

            {/* Voice Settings Link */}
            <motion.button
              onClick={() => navigate("/account/voice-settings")}
              className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] transition-all duration-200"
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.99 }}
              data-testid="voice-settings-link"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#D4A373]/15 flex items-center justify-center">
                  <Mic className="text-[#D4A373]" size={18} />
                </div>
                <div className="text-left">
                  <span className="text-[#292524] block font-medium">Voice Settings</span>
                  <span className="text-xs text-[#78716C]">Clone your voice for meditations</span>
                </div>
              </div>
              <ChevronRight size={18} className="text-[#9CA3AF]" />
            </motion.button>
            
            <motion.button 
              className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] transition-all duration-200"
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.99 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#78716C]/10 flex items-center justify-center">
                  <HelpCircle className="text-[#78716C]" size={18} />
                </div>
                <span className="text-[#292524] font-medium">Support</span>
              </div>
              <ChevronRight size={18} className="text-[#9CA3AF]" />
            </motion.button>

            {/* Revisit Introduction */}
            <motion.button
              onClick={() => {
                localStorage.removeItem('reclaim_onboarding_seen');
                window.location.href = '/';
              }}
              className="w-full p-4 flex items-center justify-between hover:bg-[#F9FAFB] transition-all duration-200"
              whileHover={{ x: 2 }}
              whileTap={{ scale: 0.99 }}
              data-testid="revisit-intro-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#D4A373]/10 flex items-center justify-center">
                  <RotateCcw className="text-[#D4A373]" size={18} />
                </div>
                <div className="text-left">
                  <span className="text-[#292524] block font-medium">Revisit Introduction</span>
                  <span className="text-xs text-[#78716C]">Experience the welcome flow again</span>
                </div>
              </div>
              <ChevronRight size={18} className="text-[#9CA3AF]" />
            </motion.button>
          </Card>
        </motion.div>

        {/* Admin Link */}
        {user?.role === "admin" && (
          <motion.div variants={fadeInUp}>
            <Button variant="outline" onClick={() => navigate("/admin")} className="w-full" data-testid="admin-link">
              <Settings size={16} /> Admin Dashboard
            </Button>
          </motion.div>
        )}

        {/* Logout */}
        <motion.div variants={fadeInUp}>
          <motion.button 
            onClick={handleLogout} 
            className="w-full py-3.5 rounded-full text-red-600 font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            data-testid="logout-btn"
          >
            <LogOut size={18} /> Sign Out
          </motion.button>
        </motion.div>

        {/* Footer Branding */}
        <motion.div variants={fadeInUp}>
          <FooterBranding />
        </motion.div>
      </motion.div>
    </PageWrapper>
  );
};
